/**
 * Push Notification Service for Taraanaya
 * ========================================
 * Handles FCM and Web Push registration
 */

import { Capacitor } from '@capacitor/core';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Firebase Messaging - only available on web
let messaging = null;

// Safe initialization - only on web platform
if (typeof window !== 'undefined' && !Capacitor.isNativePlatform()) {
  try {
    // Only import on web
    const firebaseMessaging = require('firebase/messaging');
    const firebaseApp = require('../config/firebase').default;
    messaging = firebaseMessaging.getMessaging(firebaseApp);
  } catch (e) {
    console.log('Firebase Messaging not available:', e.message);
  }
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    console.log('Notifications not supported');
    return false;
  }
  
  try {
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission === 'granted';
  } catch (e) {
    console.log('Permission request failed:', e);
    return false;
  }
}

/**
 * Register service worker for push notifications
 */
export async function registerServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    console.log('Service workers not supported');
    return null;
  }

  try {
    // Clean up legacy notifications-only SW if still registered
    try {
      const existing = await navigator.serviceWorker.getRegistrations();
      for (const reg of existing) {
        if (reg.active && reg.active.scriptURL.includes('sw-notifications.js')) {
          await reg.unregister();
          console.log('Unregistered legacy sw-notifications.js');
        }
      }
    } catch (_) { /* noop */ }

    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    console.log('Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Get FCM token for push notifications
 */
export async function getFCMToken(userId) {
  if (!messaging) {
    console.log('Firebase Messaging not available');
    return null;
  }
  
  try {
    const permissionGranted = await requestNotificationPermission();
    if (!permissionGranted) {
      console.log('Notification permission denied');
      return null;
    }
    
    const { getToken } = require('firebase/messaging');
    const token = await getToken(messaging, {
      vapidKey: 'BLBx-hf2WrL0WRY1V4C4H4T6HmWpHQJCfOx9d_Iw7K8cGzSJA9VTkJaACmjKH3tYHK9e1OEexMz6sDj3i6F8JZE'
    });
    
    if (token) {
      console.log('FCM Token:', token.substring(0, 20) + '...');
      await registerTokenWithBackend(userId, token, 'fcm');
      return token;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
}

/**
 * Subscribe to Web Push notifications
 */
export async function subscribeToWebPush(userId) {
  try {
    const permissionGranted = await requestNotificationPermission();
    if (!permissionGranted) {
      return null;
    }
    
    const registration = await registerServiceWorker();
    if (!registration) {
      return null;
    }
    
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array('BLBx-hf2WrL0WRY1V4C4H4T6HmWpHQJCfOx9d_Iw7K8cGzSJA9VTkJaACmjKH3tYHK9e1OEexMz6sDj3i6F8JZE')
    });
    
    console.log('Web Push subscription:', subscription);
    
    const subscriptionJSON = subscription.toJSON();
    await registerTokenWithBackend(userId, JSON.stringify(subscriptionJSON), 'webpush');
    
    return subscription;
  } catch (error) {
    console.error('Web Push subscription failed:', error);
    return null;
  }
}

/**
 * Convert VAPID key to Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Register token with backend
 */
async function registerTokenWithBackend(userId, token, tokenType) {
  try {
    const response = await fetch(`${BACKEND_URL}/api/notifications/register-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        token: token,
        token_type: tokenType,
        device_info: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language
        }
      })
    });
    
    const data = await response.json();
    console.log('Token registered:', data);
    return data.success;
  } catch (error) {
    console.error('Failed to register token:', error);
    return false;
  }
}

/**
 * Initialize push notifications for a user
 */
export async function initializePushNotifications(userId) {
  if (!userId) {
    console.log('No user ID provided');
    return false;
  }
  
  // Skip on native platforms - they use native push
  if (Capacitor.isNativePlatform()) {
    console.log('📱 Native platform - skipping web push init');
    return true;
  }
  
  console.log('🔔 Initializing push notifications for user:', userId);
  
  const fcmToken = await getFCMToken(userId);
  const webPushSub = await subscribeToWebPush(userId);
  
  const success = !!(fcmToken || webPushSub);
  
  if (success) {
    console.log('✅ Push notifications initialized');
    localStorage.setItem('notifications_enabled', 'true');
  } else {
    console.log('⚠️ Push notifications not available');
  }
  
  return success;
}

/**
 * Listen for foreground messages (when app is open)
 */
export function setupForegroundMessageHandler(callback) {
  if (!messaging) return;
  
  try {
    const { onMessage } = require('firebase/messaging');
    onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      
      if (callback) {
        callback(payload);
      }
      
      if (typeof document !== 'undefined' && document.hidden && Notification.permission === 'granted') {
        new Notification(payload.notification?.title || 'Taraanaya', {
          body: payload.notification?.body || 'You have a new message',
          icon: '/sage-final.png'
        });
      }
    });
  } catch (e) {
    console.log('Foreground handler not available');
  }
}

/**
 * Check if notifications are enabled for user
 */
export async function checkNotificationStatus(userId) {
  try {
    const response = await fetch(`${BACKEND_URL}/api/notifications/status/${userId}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error checking notification status:', error);
    return { notifications_enabled: false };
  }
}

/**
 * Unsubscribe from notifications
 */
export async function unsubscribeFromNotifications(userId) {
  try {
    const response = await fetch(`${BACKEND_URL}/api/notifications/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId })
    });
    
    const data = await response.json();
    localStorage.removeItem('notifications_enabled');
    localStorage.removeItem('notifications_initialized');
    return data.success;
  } catch (error) {
    console.error('Error unsubscribing:', error);
    return false;
  }
}
